import React from 'react';
import { Calculator, TrendingDown, DollarSign, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const ROICalculator = () => {
  const [inputs, setInputs] = React.useState({
    monthlySaaS: '',
    userCount: '',
    buildMonths: 6
  });
  
  const [results, setResults] = React.useState(null);

  const handleInputChange = (name, value) => {
    setInputs({
      ...inputs,
      [name]: value
    });
  };

  const calculateROI = () => {
    const monthly = parseFloat(inputs.monthlySaaS) || 0;
    const months = parseInt(inputs.buildMonths) || 6;
    const users = parseInt(inputs.userCount) || 1;

    if (monthly < 5000) {
      alert('Our 6-month model typically works best for companies spending $5,000+ per month on SaaS.');
      return;
    }

    // Calculations
    const annualSaasCost = monthly * 12;
    const totalBuildCost = monthly * months;
    const maintenanceMonthly = monthly * 0.15; // 15% of original spend
    const maintenanceAnnual = maintenanceMonthly * 12;
    const firstYearSavings = annualSaasCost - totalBuildCost - maintenanceAnnual;
    const threeYearSavings = (annualSaasCost * 3) - totalBuildCost - (maintenanceAnnual * 3);
    const fiveYearSavings = (annualSaasCost * 5) - totalBuildCost - (maintenanceAnnual * 5);
    const costPerUser = totalBuildCost / users;
    const breakEvenMonths = Math.ceil(totalBuildCost / (monthly - maintenanceMonthly));

    setResults({
      monthly,
      annualSaasCost,
      totalBuildCost,
      maintenanceMonthly,
      maintenanceAnnual,
      firstYearSavings,
      threeYearSavings,
      fiveYearSavings,
      costPerUser,
      breakEvenMonths,
      buildMonths: months
    });
  };

  return (
    <section className="py-20 bg-gradient-to-br from-[#1F6FB2]/5 via-white to-[#7AC943]/5" id="roi-calculator">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[#7AC943]/10 px-6 py-3 rounded-full mb-4">
              <Calculator className="h-5 w-5 text-[#7AC943]" />
              <span className="text-[#1F6FB2] font-semibold">Interactive Calculator</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0E3A5F] mb-4">
              Calculate Your SaaS Exit ROI
            </h2>
            <p className="text-xl text-gray-600">
              See exactly how much you could save by switching to custom software
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Input Section */}
            <div className="bg-white p-8 rounded-2xl shadow-xl border-2 border-gray-100">
              <h3 className="text-2xl font-bold text-[#0E3A5F] mb-6">Your Current Situation</h3>
              
              <div className="space-y-6">
                {/* Monthly SaaS Spend */}
                <div>
                  <label className="block text-sm font-semibold text-[#0E3A5F] mb-2">
                    Monthly SaaS Spend ($) *
                  </label>
                  <Input
                    type="number"
                    min="5000"
                    placeholder="8000"
                    value={inputs.monthlySaaS}
                    onChange={(e) => handleInputChange('monthlySaaS', e.target.value)}
                    className="w-full border-2 border-gray-200 focus:border-[#1F6FB2] rounded-lg text-lg"
                  />
                  <p className="text-xs text-gray-500 mt-1">Minimum $5,000/month</p>
                </div>

                {/* User Count */}
                <div>
                  <label className="block text-sm font-semibold text-[#0E3A5F] mb-2">
                    Number of Users *
                  </label>
                  <Input
                    type="number"
                    min="1"
                    placeholder="20"
                    value={inputs.userCount}
                    onChange={(e) => handleInputChange('userCount', e.target.value)}
                    className="w-full border-2 border-gray-200 focus:border-[#1F6FB2] rounded-lg text-lg"
                  />
                </div>

                {/* Build Timeline */}
                <div>
                  <label className="block text-sm font-semibold text-[#0E3A5F] mb-2">
                    Build Timeline
                  </label>
                  <Select 
                    value={inputs.buildMonths.toString()} 
                    onValueChange={(value) => handleInputChange('buildMonths', value)}
                  >
                    <SelectTrigger className="w-full border-2 border-gray-200 focus:border-[#1F6FB2] rounded-lg">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="6">6 months (Standard)</SelectItem>
                      <SelectItem value="9">9 months (Complex)</SelectItem>
                      <SelectItem value="12">12 months (Enterprise)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  onClick={calculateROI}
                  className="w-full bg-[#7AC943] hover:bg-[#6AB833] text-white py-6 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  Calculate My Savings
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </div>

            {/* Results Section */}
            <div className="bg-gradient-to-br from-[#0E3A5F] to-[#1F6FB2] p-8 rounded-2xl shadow-xl text-white">
              {!results ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <Calculator className="h-20 w-20 mx-auto mb-4 text-white/50" />
                    <p className="text-xl text-white/70">
                      Enter your details to see your personalized ROI projection
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  <h3 className="text-2xl font-bold mb-6">Your Projected Savings</h3>

                  {/* Break-Even */}
                  <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl border border-white/20">
                    <div className="text-sm text-white/80 mb-1">Break-Even Point</div>
                    <div className="text-3xl font-bold text-[#7AC943]">
                      {results.breakEvenMonths} months
                    </div>
                    <div className="text-xs text-white/70 mt-1">After your {results.buildMonths}-month build</div>
                  </div>

                  {/* Year 1 */}
                  <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl border border-white/20">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-white/80">Year 1 Net Savings</span>
                      <TrendingDown className="h-5 w-5 text-[#7AC943]" />
                    </div>
                    <div className="text-3xl font-bold">
                      ${results.firstYearSavings.toLocaleString()}
                    </div>
                    <div className="text-xs text-white/70 mt-2">
                      SaaS: ${results.annualSaasCost.toLocaleString()}/yr → 
                      You: ${(results.totalBuildCost + results.maintenanceAnnual).toLocaleString()}/yr
                    </div>
                  </div>

                  {/* 3-Year Savings */}
                  <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl border border-white/20">
                    <div className="text-sm text-white/80 mb-1">3-Year Total Savings</div>
                    <div className="text-3xl font-bold text-[#7AC943]">
                      ${results.threeYearSavings.toLocaleString()}
                    </div>
                  </div>

                  {/* 5-Year Savings */}
                  <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl border border-white/20">
                    <div className="text-sm text-white/80 mb-1">5-Year Total Savings</div>
                    <div className="text-3xl font-bold text-[#7AC943]">
                      ${results.fiveYearSavings.toLocaleString()}
                    </div>
                  </div>

                  {/* Cost per User */}
                  <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl border border-white/20">
                    <div className="text-sm text-white/80 mb-1">One-Time Cost Per User</div>
                    <div className="text-2xl font-bold">
                      ${results.costPerUser.toLocaleString()}
                    </div>
                  </div>

                  {/* Maintenance */}
                  <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl border border-white/20">
                    <div className="text-sm text-white/80 mb-1">Ongoing Maintenance</div>
                    <div className="text-2xl font-bold">
                      ${results.maintenanceMonthly.toLocaleString()}/month
                    </div>
                    <div className="text-xs text-white/70 mt-1">
                      85% less than current SaaS spend
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Disclaimer */}
          {results && (
            <div className="mt-8 text-center">
              <p className="text-sm text-gray-600 max-w-3xl mx-auto">
                * These are estimated savings based on typical projects. Your actual results may vary depending on project complexity, features required, and specific integrations needed. Schedule a free consultation for a detailed assessment.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ROICalculator;
